
import { toast } from "@/components/ui/sonner";
import { removeAuthToken } from '@/utils/authUtils';

// Frontend utility for handling API responses
interface APIResponse {
  error?: string;
  message?: string;
  requiresAuth?: boolean;
  isGuestError?: boolean;
  searchCount?: number;
  maxSearches?: number;
  remainingSearches?: number;
}

export function handleAPIError(response: APIResponse) {
  if (response.isGuestError && response.error === 'GUEST_SEARCH_LIMIT_EXCEEDED') {
    toast.warning(
      `You've used all ${response.maxSearches} guest searches. Sign up for unlimited searches!`
    );
    return { shouldRedirectToAuth: true };
  }

  if (response.requiresAuth) {
    toast.error(response.message || 'Authentication required');
    return { shouldRedirectToAuth: true };
  }

  toast.error(response.message || 'An error occurred');
  return { shouldRedirectToAuth: false };
}

// Global API error handler for HTTP responses
export class ApiError extends Error {
  status: number;
  
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

/**
 * Global error handler for HTTP responses
 * Automatically handles 401/403 errors by clearing auth state and redirecting to login
 */
export const handleHttpError = async (response: Response): Promise<never> => {
  // Handle authentication/authorization errors
  if (response.status === 401 || response.status === 403) {
    console.warn(`🔐 [AUTH_ERROR] ${response.status} error detected - clearing session`);
    
    // Clear authentication state
    removeAuthToken();
    localStorage.removeItem("userName");
    localStorage.removeItem("userId");
    
    // Clear session service if available
    try {
      const { sessionService } = await import('@/services/sessionService');
      sessionService.clearSession();
    } catch (e) {
      // sessionService might not be available, that's ok
    }
    
    // Show user-friendly message
    toast.error('Your session has expired. Please log in again.');
    
    // Redirect to login page
    console.log('🔄 [AUTH_ERROR] Redirecting to login page');
    window.location.href = '/login';
    
    throw new ApiError('Authentication required', response.status);
  }
  
  // Handle other errors
  const errorMessage = `Request failed: ${response.status}`;
  throw new ApiError(errorMessage, response.status);
};

/**
 * Enhanced fetch wrapper that automatically handles auth errors
 */
export const authenticatedFetch = async (
  url: string, 
  options: RequestInit = {}
): Promise<Response> => {
  const response = await fetch(url, options);
  
  if (!response.ok) {
    await handleHttpError(response);
  }
  
  return response;
};
